import { EnvironmentInjector, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { BehaviorSubject, EMPTY, firstValueFrom, interval, Observable } from 'rxjs';
import { ClientSettingsType, Currency, FeatureFlags, FrontendPermissions, ICheckoutRegisterConfig, ICredentialInfo,
  IUser, NotifyChannel, Permission, RPC } from '@vierkant-software/types__api';
import { ApiService } from './api.service';
import { v4 as uuid } from 'uuid';
import { IPage } from 'src/app/page.interface';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, NavigationExtras, Router, ResolveFn } from '@angular/router';
import { ExitState } from 'src/app/modules/ngx-draft';
import { Permissions } from 'src/util/Permissions';
import { Subscription } from 'rxjs';
import { PageService } from './page.service';
import { onModalStateChanged } from 'src/util/types/hooks';

import { ConfirmationService, MessageService } from 'primeng/api';
import { AppComponent } from 'src/app/app.component';
import { ErrorTranslations, error_translations } from 'src/environments/errors.de';
import { BitField } from 'src/util/bitfield/bitfield';
import { SettingsService } from './settings.service';
import { IDraftConflict, DraftConflictResolution } from 'src/app/modules/ngx-draft/types/IAppService';
import { APP_VERSION } from 'src/environments/version';
import type { WorkFlowService } from 'src/app/modules/ngx-workflow';
import { UserSyncService } from 'src/app/services/userSync.service';
import { VTranslateService } from './v-translate.service';
import { PrimeNG } from 'primeng/config';
import { ProductDetailLevel, ProductSyncService } from 'src/app/services/productSync.service';
import { ShoppingCartDetailLevel, ShoppingCartSyncService } from 'src/app/services/shoppingCartSync.service';
import { ProductCategoryDetailLevel, ProductCategorySyncService } from '../app/services/productCategorySync.service';
import { CheckInDetailLevel, CheckInSyncService } from 'src/app/services/checkInSync.service';
import { CheckOutRegisterSessionDetailLevel, CheckOutRegisterSessionSyncService } from 'src/app/services/checkOutRegisterSessionSync.service';
import { environment } from 'src/environments/environment';
import { DebugService } from './debug.service';
import { DepartmentSyncService } from 'src/app/services/departmentSync.service';
//#region API State definitions
export enum APIStates {
  loggedOut,
  disconnected,
  connected,
  loggedIn,
  switchingStudio,
  externalStudio,
}

export type APIState =
  { type: APIStates.loggedOut } |
  { type: APIStates.disconnected } |
  { type: APIStates.connected } & IStudioInfo |
  { type: APIStates.loggedIn } & IStudioInfo & IUserInfo |
  { type: APIStates.switchingStudio } & IStudioInfo |
  { type: APIStates.externalStudio } & IStudioInfo & IUserInfo
;
//#endregion

export interface IStudioInfo {
  studio: string;
  studioName: string;
  studioLogo: SafeUrl;
  currencies: Currency[];
}

export interface IUserInfo {
  user: IUser;
  avatar?: SafeUrl;
}

export type Language = string;
class Languages {
  static DE_DE: Language = "de-DE";
  static EN_US: Language =  "en-US";
}

export abstract class AppService {
  public static instance: AppService;
  protected abstract apiService: ApiService;
  protected abstract router: Router;
  protected abstract modal_show: boolean;
  protected abstract rootActivatedRoute: ActivatedRoute;
  public abstract pageService: PageService;
  public abstract translate: TranslateService;
  public abstract vtranslate: VTranslateService;
  public workflowService: WorkFlowService;
  protected abstract location: Location;
  public abstract environmentInjector: EnvironmentInjector;

  #userSyncService: UserSyncService;
  get userSyncService() {
    return this.#userSyncService ?? (this.#userSyncService = this.environmentInjector.get(UserSyncService));
  }

  #departmentSyncService: DepartmentSyncService;
  get departmentSyncService() {
    return this.#departmentSyncService ?? (this.#departmentSyncService = this.environmentInjector.get(DepartmentSyncService));
  }

  #productSyncService: ProductSyncService;
  get productSyncService() {
    return this.#productSyncService ?? (this.#productSyncService = this.environmentInjector.get(ProductSyncService));
  }

  #shoppingCartSyncService: ShoppingCartSyncService;
  get shoppingCartSyncService() {
    return this.#shoppingCartSyncService ?? (this.#shoppingCartSyncService = this.environmentInjector.get(ShoppingCartSyncService));
  }

  #productCategorySyncService: ProductCategorySyncService;
  get productCategorySyncService() {
    return this.#productCategorySyncService ?? (this.#productCategorySyncService = this.environmentInjector.get(ProductCategorySyncService));
  }

  #checkInSyncService: CheckInSyncService;
  get checkInSyncService() {
    return this.#checkInSyncService ?? (this.#checkInSyncService = this.environmentInjector.get(CheckInSyncService));
  }

  #checkOutRegisterSessionSyncService: CheckOutRegisterSessionSyncService;
  get checkOutRegisterSessionSyncService() {
    return this.#checkOutRegisterSessionSyncService
      ?? (this.#checkOutRegisterSessionSyncService = this.environmentInjector.get(CheckOutRegisterSessionSyncService));
  }

  protected cashRegisterConfig: {
    studioId: string;
    cashRegisterId: string;
    configToken: string;
  } | null = null;

  public checkoutRegisterConfig: ICheckoutRegisterConfig | null = null;

  /** the till config can change while the till runs, e.g. cash removed from its payment types */
  public async reloadCheckoutRegisterConfig(): Promise<void> {
    this.checkoutRegisterConfig = await this.deviceApi.CheckOutWorker.getCheckOutRegisterConfig();
  }

  public modal_options: {
    isModal?: boolean;
    closeBackdrop?: boolean;
    closeOnEscape?: boolean;
    height?: string;
    width?: string;
    overflow?: string;
    exitState?: ExitState;
  } = {};
  protected mainComponent: BehaviorSubject<IPage & onModalStateChanged>;
  protected apiState: APIStates;
  protected $apiState: BehaviorSubject<APIState>;
  protected __locale: string;
  protected $locale: BehaviorSubject<string>;
  protected __mainAPI: RPC;
  protected __mainStudio: string;
  // the till's device session from preAuth or refreshSession, the till syncs run over it
  protected $syncSession = new BehaviorSubject<string | null>(null);
  protected currentPermissions: Uint8Array;
  protected currentFrontendPermissions: Uint8Array;
  protected maxPermissions: Uint8Array;
  protected features = signal(new BitField(0));
  #deviceId: string;
  protected studioInfos: { [studio: string]: IStudioInfo } = {};
  public abstract messageService: MessageService;
  public get languages(): Language[] {
    return [Languages.DE_DE, Languages.EN_US];
  }
  protected get defaultLanguage(): Language{
    const availableLanguages = this.languages;
    let lang = localStorage.getItem("lang") ?? navigator.language;
    lang = availableLanguages.find(x => lang && x.startsWith(lang));
    if (!lang)
      lang = availableLanguages[0];
    localStorage.setItem("lang", lang);
    return lang;
  }

  public abstract getTranslation(key: string): unknown;
  public abstract confirmationService: ConfirmationService;

  public get state(): APIStates {
    return this.apiState ?? APIStates.disconnected;
  }

  clientIsValid = signal(true);
  public theme = signal<'hmm' | undefined>(undefined);
  public hideMenu = signal<boolean>(false);
  public primengConfig?: PrimeNG;

  //#region routerOutlet component awareness
  public get $component(): Observable<IPage> {
    return this.mainComponent.asObservable();
  }
  //#endregion

  public get $events() {
    return this.apiService.$events;
  }

  public get $connectionStatus() {
    return this.apiService.$status;
  }

  //#region API
  public get $APIState() {
    return this.$apiState.asObservable();
  }

  public get $Locale() {
    return this.$locale.asObservable();
  }

  public get locale() {
    return this.__locale;
  }

  public get $SyncSession() {
    return this.$syncSession.asObservable();
  }

  public get syncSession() {
    return this.$syncSession.value;
  }

  get deviceApi(): RPC {
    return this.apiService.getInterface(this.__mainStudio, this.$syncSession.value);
  }

  get api(): RPC {
    if (!this.__mainAPI)
      throw new Error('not connected.');
    return this.__mainAPI;
  }

  public get deviceId() {
    if (this.#deviceId) return this.#deviceId;
    this.#deviceId = localStorage.getItem('deviceId');
    // console.log('deviceId:', this.#deviceId);
    if (!this.#deviceId) {
      this.#deviceId = uuid();
      // console.log('deviceId new:', this.#deviceId);
      localStorage.setItem('deviceId', this.#deviceId);
    }
    return this.#deviceId;
  }

  public get myUserId() {
    const state = this.$apiState.getValue();
    if (state.type === APIStates.loggedIn)
      return state.user.ID;
    return undefined;
  }
  //#endregion

  get studioInfo(): IStudioInfo {
    if (this.__mainStudio)
      return this.studioInfos[this.__mainStudio];
    return;
  }

  get defaultCurrency(): Currency {
    return this.studioInfo?.currencies?.[0] ?? Currency.EUR;
  }

  //#region Permissions
  public getPermission(perm: Permission): boolean {
    // eslint-disable-next-line no-bitwise
    return (this.currentPermissions[perm / 8 |0] & (1 << (perm % 8))) > 0;
  }

  public getPermissions(perms: Permission[]): boolean {
    return perms.some(x => this.getPermission(x));
  }

  public getFrontendPermission(perm: FrontendPermissions): boolean {
    // eslint-disable-next-line no-bitwise
    return (this.currentFrontendPermissions[perm / 8 |0] & (1 << (perm % 8))) > 0;
  }

  public getFrontendPermissions(perms: FrontendPermissions[]): boolean {
    return perms.some(x => this.getFrontendPermission(x));
  }

  public getMissingFrontendPermissions(perms: FrontendPermissions[]): string {
    return perms.filter(x => !this.getFrontendPermission(x)).map(x => FrontendPermissions[x]).join(', ');
  }

  public getFeature(feature: FeatureFlags): boolean {
    return this.features().isSet(feature);
  }
  //#endregion

  public async navigateModal(uri: string[] | null, options?: AppService['modal_options'], extras: NavigationExtras = {}) {
    this.modal_show = !!uri;
    this.modal_options = options ?? {};
    await this.router.navigate([{ outlets: { modal: uri }}], {...extras, ...{relativeTo: this.rootActivatedRoute.parent, skipLocationChange: true} });
    return this.mainComponent?.value?.onModalStateChanged?.(!uri, options?.exitState);
  }

  public async reloadCurrentUrl() {
    const url = this.location.pathname;
    await this.router.navigate(['/empty'], { skipLocationChange: true });
    return this.router.navigateByUrl(url, { skipLocationChange: true });
  }

  public async reRunResolvers(activatedRoute: ActivatedRoute, environmentInjector?: EnvironmentInjector) {
    let result: Record<string, unknown> = {};
    AppComponent.instance.loading = true;
    for (const route of activatedRoute.pathFromRoot) {
      if (route.routeConfig?.data)
        result = { ...result, ...route.routeConfig.data };
      if (route.routeConfig?.resolve) {
        await Promise.all(
          Object.entries(route.routeConfig.resolve).map(async ([key, resolver]) => {
            let instance: {
              resolve: ResolveFn<unknown>;
            };
            if (typeof resolver.prototype?.constructor?.name === 'string') {
              const injector = environmentInjector ?? Injector.create({ providers: [resolver], parent: this.environmentInjector });
              instance = injector.get(resolver, undefined, { optional: true, skipSelf: true });
            }
            const method = instance?.resolve ?? resolver;
            const res = runInInjectionContext(
              (environmentInjector ?? this.environmentInjector),
              () => method.apply(instance, [activatedRoute.snapshot, this.router.routerState.snapshot, true])
            );
            console.log(res);
            if (res === EMPTY)
              throw new Error('Error while running ' + key + ' in route ' + route.routeConfig);
            else if (res === undefined)
              return; // Ignore undefined results
            else if (res instanceof Promise)
              result[key] = await res;
            else if (res instanceof Observable)
              result[key] = await firstValueFrom(res);
            else
              result[key] = res;
          })
        );
      }
    }
    console.log(result);
    if (Object.keys(result).length > 0)
      (activatedRoute.data as BehaviorSubject<unknown>).next(result);
    AppComponent.instance.loading = false;
  }

  /** query a client specific setting from SettingService.
   *  This is ment to be used in resolvers. In pages/components use @GcSetting decorator instead.
   */
  getSetting<T>(type: ClientSettingsType): T{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = SettingsService.instance.settings[type] as any;
    if (typeof value === 'object' && !value.isLuxonDateTime && !value.isLuxonDuration)
      return SettingsService.instance.getObjectProxy(type) as T;
    else return SettingsService.instance.settings[type] as T;
  }

  async resolveDuplicatedLockIdentifier(conflicts: IDraftConflict[], displayInfo: undefined | never): Promise<DraftConflictResolution> {
    return await AppComponent.instance.raiseLockIssue(conflicts);
  }

  //#region Channel subscription
  #channelResubs = interval(60 * 60 * 1000).subscribe(() => {
    if (this.state !== APIStates.loggedIn)
      return;
    this.channels.forEach(channel => {
      this.api.CommWorker.subscribeToChannel(channel).catch(console.error);
    });
  });

  protected _channels: { [key in NotifyChannel]?: number } = {};

  public get channels(): NotifyChannel[] {
    return Object.keys(this._channels)
      .map(x => +x as NotifyChannel)
      .filter(x => this._channels[x] > 0);
  }

  public subscribeChannel(channel: NotifyChannel): boolean {
    if (!this._channels[channel]) {
      this._channels[channel] = 1;
      this.api.CommWorker.subscribeToChannel(channel).catch(console.error);
      return true;
    }
     this._channels[channel]++;
    return false;
  }

  public unsubscribeChannel(channel: NotifyChannel): boolean {
    if (!this._channels[channel]) {
      console.warn('Tried to unsubscribe from channel that was not subscribed:', channel);
      return false;
    }
    this._channels[channel]--;
    return true;
  }
  //#endregion
}

@Injectable()
export class InternalAppService extends AppService {
  public rootActivatedRoute: ActivatedRoute;
  public modal_show: boolean = false;

  #isInjectedSession: boolean = false;
  #connSubs: Subscription;
  #deviceSession: string | null;
  #cashRegisterServicesStarted = false;
  #renewingSyncSession = false;
  #syncSessionResubs = interval(60 * 60 * 1000).subscribe(() => {
    if (this.syncSession)
      this.deviceApi.CommWorker.subscribeToChannel(NotifyChannel.sync).catch(console.error);
  });

  public get injectedSession() {
    return this.#isInjectedSession;
  }

  constructor(
    protected apiService: ApiService,
    private sanitizer: DomSanitizer,
    public translate: TranslateService,
    public vtranslate: VTranslateService,
    protected router: Router,
    public pageService: PageService,
    public messageService: MessageService,
    public confirmationService: ConfirmationService,
    protected location: Location,
    public environmentInjector: EnvironmentInjector,
  ) {
    super();
    AppService.instance = this;
    this.pageService.app = this;
    DebugService.instance.app = this;
    this.$apiState = new BehaviorSubject({ type: APIStates.loggedOut });
    this.mainComponent = new BehaviorSubject(undefined);
    this.currentPermissions = new Uint8Array();
    this.currentFrontendPermissions = new Uint8Array();
    this.setupLocale();

    if (environment.isCashRegister) {
      // TODO: Put that back in when hmm theme is completely implemented
      //this.theme.set('hmm');
      this.hideMenu.set(true);
    }

    this.apiService.$status.subscribe(x => {
      switch(x) {
        case 'connected':
          this.__mainAPI.AuthWorker.validateClientVersion(
            environment.isCashRegister ? "cashregister" : "admin", APP_VERSION).then(x => this.clientIsValid.set(x)
          ).catch(console.error);
          void this.#registerSyncSession();
          break;
      }
    });
  }

  private setupLocale(){
    try {
      this.__locale = this.defaultLanguage;
      this.translate.addLangs(this.languages);
      this.translate.use(this.defaultLanguage);
      this.vtranslate.loadTranslation('de-DE').catch(console.error);
      this.$locale = new BehaviorSubject(this.__locale);
    } catch(error) {
      console.error(error);
    }
  }

  public __activate(component: IPage, inModal = false) {
    if (!component)
      return console.warn("Warning: activating undefined component");
    if (inModal)
      return false;
    this.mainComponent.next(component);
    this.pageService.__activate(component).catch(console.error);
  }

  public getTranslation(key: string) {
    return this.primengConfig.getTranslation(key) as string;
  }

  // now called before the bootstrapping of the app is complete
  async __initAPI() {
    this.apiState = APIStates.disconnected;

    let studio;
    if (environment.isCashRegister) {
      const cashRegisterConfig = localStorage.getItem('cashRegisterConfig');
      if (!cashRegisterConfig) {
        this.#setAPIstate({ type: APIStates.disconnected });
        return;
      }
      this.cashRegisterConfig = JSON.parse(cashRegisterConfig);
      this.#setAPIstate({ type: APIStates.connected, ...this.studioInfos[this.cashRegisterConfig?.studioId] });
      await this.__setStudio(this.cashRegisterConfig.studioId);
      this.__mainAPI = this.apiService.getInterface(this.__mainStudio, this.#deviceSession);

      let currentDeviceOwnsCashRegister;
      try {
        currentDeviceOwnsCashRegister = await this.api.CheckOutWorker.validateCheckoutStillRegisteredToDevice(this.cashRegisterConfig.cashRegisterId);
      } catch(e) {
        console.error('Error validating cash register ownership:', e);
        currentDeviceOwnsCashRegister = false;
      }

      if (!currentDeviceOwnsCashRegister) {
        localStorage.removeItem('cashRegisterConfig');
        this.#setAPIstate({ type: APIStates.disconnected });
        await this.router.navigate(['/config']).catch(console.error);
        return;
      }

      studio = this.cashRegisterConfig.studioId;
      this.checkoutRegisterConfig = await this.api.CheckOutWorker.getCheckOutRegisterConfig();
    } else {
      // Member box flow (existing logic)
      studio = localStorage.getItem('studio');
      if (!studio) return;
      await this.__setStudio(studio);
    }

    if (localStorage.getItem('__debug') && await this.__loadSession(studio)) return;
    /*
    if (!this.getPermission(1)) return;     //TODO permission autologin
    const refreshtoken = localStorage.getItem('refreshtoken');
    if (!refreshtoken) return;
    this.__login(refreshtoken);     //TODO credentialType.refreshtoken
    */
  }

  #setAPIstate(state: APIState) {
    this.apiState = state.type;
    this.$apiState.next(state);
  }

  // the device session outlives the user session, so it registers for the sync events on its own.
  // The new one is subscribed before the switch, so no events get lost.
  async #useSyncSession(session: string | null | undefined) {
    if (!environment.isCashRegister || !session || session === this.syncSession)
      return;
    await this.#registerSyncSession(session);
    if (session === this.syncSession)
      return;
    const oldSession = this.syncSession;
    this.$syncSession.next(session);
    // a later login reuses it, an unused older one may have expired by then
    this.#deviceSession = session;
    if (!oldSession)
      return;
    const oldAPI = this.apiService.getInterface(this.__mainStudio, oldSession);
    oldAPI.CommWorker.unsubscribeFromChannel(NotifyChannel.sync).catch(console.error);
    oldAPI.CommWorker.unregisterDeviceAndSession().catch(console.error);
  }

  async #registerSyncSession(session = this.syncSession) {
    if (!session)
      return;
    const api = this.apiService.getInterface(this.__mainStudio, session);
    await api.CommWorker.registerDeviceAndSession(this.deviceId).catch(console.error);
    await api.CommWorker.subscribeToChannel(NotifyChannel.sync).catch(console.error);
  }

  #initializeCashRegisterServices() {
    if (this.#cashRegisterServicesStarted)
      return;
    this.#cashRegisterServicesStarted = true;
    Promise.all([
      this.shoppingCartSyncService.fullSync(ShoppingCartDetailLevel.default),
      this.productSyncService.fullSync(ProductDetailLevel.full),
      this.productCategorySyncService.fullSync(ProductCategoryDetailLevel.info),
      this.checkInSyncService.fullSync(CheckInDetailLevel.attendance),
      this.checkOutRegisterSessionSyncService.fullSync(CheckOutRegisterSessionDetailLevel.default),
    ]).catch((e) => {
      // the next login starts them again
      this.#cashRegisterServicesStarted = false;
      console.warn('[AppService] Failed to initialize the cash register syncs:', e);
    });
  }

  __setLocale(locale: string) {
    try {
          const lang = this.languages.find(x => locale && x.startsWith(locale));
        if (!lang){ // prevent setting invalid or undefined locale
          console.error("Invalid locale: " + locale);
          return;
        }
        localStorage.setItem("lang", lang);
        this.__locale = lang;
        this.translate.use(lang);
        this.$locale.next(lang);
    } catch(error) {
      console.error(error);
    }
  }

  public async __setStudio(studio: string) {
    if (![
      APIStates.disconnected,
      APIStates.loggedOut,
      APIStates.connected,
    ].includes(this.apiState))
      throw new Error('API is in wrong state to set an preflight studio [' + APIStates[this.apiState] + ']');
    this.__mainAPI = this.apiService.getInterface(studio, null);
    try {
      const info = await this.__mainAPI.AuthWorker.preAuth(this.deviceId);
      this.#deviceSession = info.deviceSession;
      this.clientIsValid.set(await this.__mainAPI.AuthWorker.validateClientVersion("admin", APP_VERSION));
      if (info.studio !== undefined && info.studio !== studio) {
        studio = info.studio;
        this.__mainAPI = this.apiService.getInterface(studio, null);
      }
      this.#setPermissions(info);
      const studioInfo = {
        studio,
        studioName: info.studioName,
        studioLogo: this.sanitizer.bypassSecurityTrustResourceUrl(info.__files[0] ?? './assets/img/placeholder/image.png'),
        currencies: info.currencies ?? ['EUR'] as Currency[],
      };
      this.studioInfos[studio] = studioInfo;
      this.__mainStudio = studio;

      this.#setAPIstate({ type: APIStates.connected, ...studioInfo });
      localStorage.setItem('studio', studio);
      return true;
    } catch(e) {
      return e;
    }
  }

  public get __Studio() {
    return localStorage.getItem('studio');
  }

  public async __login(credentials: ICredentialInfo, remember: boolean = false) {
    if (![APIStates.disconnected, APIStates.loggedOut, APIStates.connected].includes(this.apiState))
      throw new Error('API is in wrong state to login [' + APIStates[this.apiState] + ']');
    try {
      const res = await this.__mainAPI.AuthWorker.auth(credentials, this.deviceId);
      if (!Permissions.hasAllPerms([this.#loginPermission], res.permissions))
        throw new Error('no permission');
      this.#isInjectedSession = false;
      this.#setPermissions(res);
      this.__mainAPI = this.apiService.getInterface(this.__mainStudio, res.ID);
      await this.#useSyncSession(this.#deviceSession);
      this.#subscribeReconnectHandler();
      this.userSyncService; //TODO: Init services hook
      if (localStorage.getItem('__debug')) localStorage.setItem('__sessionInfo', JSON.stringify(res));

      this.#setAPIstate({
        type:   APIStates.loggedIn,
        ...this.studioInfos[this.__mainStudio],
        user:   res.user,
        avatar: res.__files[0] ? this.sanitizer.bypassSecurityTrustResourceUrl(res.__files[0]) : undefined,
      });
      if (environment.isCashRegister)
        this.#initializeCashRegisterServices();
      /* TODO refreshtoken from backend
      if (remember && session.refreshtoken)
      localStorage.setItem('refreshtoken', session.refreshtoken);
      */
     return true;
    } catch(e) {
      console.log('exception __login:', e);
      return 'login failed';
    }
  }

  async __activateSession(studio: string, sessionID: string): Promise<void> {
    try {
      if (localStorage.getItem('__debug'))
        throw new Error('Master Login is not allowed in debug mode');
      if (![APIStates.disconnected, APIStates.loggedOut, APIStates.connected].includes(this.apiState))
        throw new Error('API is in wrong state to set an preflight studio [' + APIStates[this.apiState] + ']');
      if (environment.isCashRegister && !this.cashRegisterConfig)
        throw new Error('Cash register is not configured');
      if (environment.isCashRegister && this.cashRegisterConfig?.studioId !== studio)
        throw new Error('Injected session studio does not match configured cash register studio');
      await this.__setStudio(studio);
      this.__mainAPI = this.apiService.getInterface(this.__mainStudio, sessionID);
      const res = await this.__mainAPI.AuthWorker.refreshSession(this.deviceId);
      this.__mainAPI = this.apiService.getInterface(this.__mainStudio, res.newSession);
      this.#isInjectedSession = true;
      if (!Permissions.hasAllPerms([this.#loginPermission], res.permissions))
        throw new Error('no permission');
      this.#setPermissions(res);
      await this.#useSyncSession(res.deviceSession ?? this.#deviceSession);
      this.#subscribeReconnectHandler();
      this.userSyncService;   //TODO: Init services hook
      this.#setAPIstate({
        type:   APIStates.loggedIn,
        ...this.studioInfos[this.__mainStudio],
        user:   res.user,
        avatar: res.__files[0] ? this.sanitizer.bypassSecurityTrustResourceUrl(res.__files[0]) : undefined,
      });
      if (environment.isCashRegister) {
        await this.reloadCheckoutRegisterConfig();
        this.#initializeCashRegisterServices();
      }
      this.pageService.setLoggedInOptions();
    } catch(e) {
      console.log('exception __activateSession:', e);
    }
    await this.router.navigateByUrl(environment.isCashRegister ? '/checkout/products' : '/');
  }

  async __switchSession(studio: string, sessionID: string): Promise<void> {
    await this.__logout();
    return await this.__activateSession(studio, sessionID);
  }

  /** In-place session swap - ONLY for the MemberBox cash register (isCashRegister), where users
   *  share the running page state (open cart, drafts) and only identity/permissions change.
   *  NEVER use this for the admin panel: its cached per-user state would leak across users. */
  async __swapSession(studio: string, sessionID: string): Promise<void> {
    if (this.apiState !== APIStates.loggedIn)
      return this.__activateSession(studio, sessionID);
    if (studio !== this.__mainStudio)
      throw new Error('Injected session studio does not match the active studio');
    if (environment.isCashRegister && this.cashRegisterConfig?.studioId !== studio)
      throw new Error('Injected session studio does not match configured cash register studio');
    const oldAPI = this.__mainAPI;
    this.__mainAPI = this.apiService.getInterface(this.__mainStudio, sessionID);
    let res;
    try {
      res = await this.__mainAPI.AuthWorker.refreshSession(this.deviceId);
      if (!Permissions.hasAllPerms([this.#loginPermission], res.permissions))
        throw new Error('no permission');
    } catch(e) {
      this.__mainAPI = oldAPI;
      throw e;
    }
    this.__mainAPI = this.apiService.getInterface(this.__mainStudio, res.newSession);
    this.#isInjectedSession = true;
    this.#setPermissions(res);
    await this.#useSyncSession(res.deviceSession);
    //rebind the server-side notify registration to the new session (same WS, no reconnect)
    await oldAPI.CommWorker.unregisterDeviceAndSession(this.deviceId).catch(console.error);
    await this.__mainAPI.CommWorker.registerDeviceAndSession(this.deviceId).catch(console.error);
    this.channels.forEach(channel => {
      this.api.CommWorker.subscribeToChannel(channel).catch(console.error);
    });
    this.#setAPIstate({
      type:   APIStates.loggedIn,
      ...this.studioInfos[this.__mainStudio],
      user:   res.user,
      avatar: res.__files[0] ? this.sanitizer.bypassSecurityTrustResourceUrl(res.__files[0]) : undefined,
    });
  }

  /** The cash register requires Login_Cashbox, everything else App_Login_Frontend. */
  get #loginPermission(): Permission {
    return environment.isCashRegister ? Permission.Login_Cashbox : Permission.App_Login_Frontend;
  }

  #subscribeReconnectHandler() {
    this.#connSubs = this.apiService.$status.subscribe(x => {
      switch(x) {
        case 'connected':
          this.__mainAPI.CommWorker.registerDeviceAndSession(this.deviceId)
            .catch(err => console.error(err));
          if (this.state === APIStates.loggedIn) {
            this.channels.forEach(channel => {
              this.api.CommWorker.subscribeToChannel(channel).catch(console.error);
            });
          }
          break;
      }
    });
  }

  public async __logout() {
    this.#isInjectedSession = false;
    this.#setAPIstate({ type: APIStates.loggedOut });
    await Promise.all(this.channels.map(channel => {
      this.api.CommWorker.unsubscribeFromChannel(channel).catch(console.error);
    }));
    this._channels = {};
    // the till keeps the registration of its device for the device session
    await this.api.CommWorker.unregisterDeviceAndSession(environment.isCashRegister ? undefined : this.deviceId).catch(console.error);
    this.#connSubs?.unsubscribe();
    localStorage.removeItem('refreshtoken');
    localStorage.removeItem('__sessionInfo');
  }

  private async __loadSession(studio: string) {
    try {
      await this.__setStudio(studio);
      const result = JSON.parse(localStorage.getItem('__sessionInfo'));
      if (!result?.ID) return false;
      this.#setPermissions(result);
      this.__mainAPI = this.apiService.getInterface(this.__mainStudio, result.ID);
      await this.#useSyncSession(this.#deviceSession);
      this.#subscribeReconnectHandler();
      this.#setAPIstate({
        type:   APIStates.loggedIn,
        ...this.studioInfos[studio],
        user:   result.user,
        avatar: result.__files[0] ? this.sanitizer.bypassSecurityTrustResourceUrl(result.__files[0]) : undefined
      });
      if (environment.isCashRegister)
        this.#initializeCashRegisterServices();
      this.pageService.setLoggedInOptions();
    //   console.log('sid:', result.ID);
      return true;
    } catch(e) {
      console.log('exception __loadSession:', e);
      return false;
    }
  }

  #setPermissions(session: { permissions: Uint8Array, frontendPermissions?: Uint8Array, features?: string }) {
    this.currentPermissions = session.permissions;
    if (session.frontendPermissions !== undefined)
      this.currentFrontendPermissions = session.frontendPermissions;
    if (session.features !== undefined)
      this.features.set(new BitField(session.features, 0));
    this.maxPermissions = session.permissions;  // TODO backend
  }

  //#region Resolver Exception Handling
  #resolverExceptions: Record<string, string[]> = {};

  public __clearResolverException(outlet: string) {
    delete this.#resolverExceptions[outlet];
  }

  public __addResolverException(outlet: string, msg: string) {
    if (!this.#resolverExceptions[outlet])
      this.#resolverExceptions[outlet] = [];
    this.#resolverExceptions[outlet].push(msg);
  }

  public __getResolverExceptions() {
    const ex = Object.values(this.#resolverExceptions).flat(1);
    this.#resolverExceptions = {};
    return ex;
  }
  //#endregion

  async invalidSession() {
    await this.__logout();
    this.messageService.add({
      severity: "warn",
      summary:  (error_translations.ADMIN as ErrorTranslations).INVALID_SESSION_HEADER as string,
      detail:   (error_translations.ADMIN as ErrorTranslations).INVALID_SESSION as string,
    });
    this.router.navigate(['login']).catch(console.error);
  }

  // the device session expires after days without calls, the till then fetches a new one
  async renewSyncSession() {
    if (this.#renewingSyncSession)
      return;
    this.#renewingSyncSession = true;
    try {
      const info = await this.apiService.getInterface(this.__mainStudio, null).AuthWorker.preAuth(this.deviceId);
      await this.#useSyncSession(info.deviceSession);
    } finally {
      this.#renewingSyncSession = false;
    }
  }

  async maintenance() {
    await this.__logout();
    this.router.navigate(['/maintenance'], { skipLocationChange: true }).catch(console.error);
  }

  async activateCheckout(shortUrl: string, force: boolean = false): Promise<boolean> {
    const preflightApi = this.apiService.getInterface(environment.preflightStudio, null);
    const jwt = await preflightApi.CommWorker.expandShortUrl(shortUrl);
    const parts = jwt.split('.');

    if (parts.length !== 3)
      return false;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const decodedJwt = JSON.parse(atob(padded));

    this.cashRegisterConfig = {
      studioId:       decodedJwt.st,
      cashRegisterId: decodedJwt.c,
      configToken:    shortUrl
    };

    await this.__setStudio(this.cashRegisterConfig.studioId);
    await this.api.CheckOutWorker.activateCheckOut(shortUrl, this.deviceId, force);
    localStorage.setItem('cashRegisterConfig', JSON.stringify(this.cashRegisterConfig));
    const res = await this.api.AuthWorker.preAuth(this.deviceId);
    this.#deviceSession = res.deviceSession;
    this.__mainAPI = this.apiService.getInterface(this.__mainStudio, this.#deviceSession);
    this.checkoutRegisterConfig = await this.api.CheckOutWorker.getCheckOutRegisterConfig();
    return true;
  }

  public async validateConfigToken(token: string): Promise<boolean> {
    try {
      return await this.activateCheckout(token);
    } catch(e) {
      if (e?.status === 'INVALID:CHECKOUT_REGISTER_ALREADY_ASSIGNED') {
        return new Promise((resolve) => {
          this.confirmationService.confirm({
            header:  'Kasse bereits zugewiesen',
            message: 'Diese Kasse ist bereits einem anderen Gerät zugewiesen. Möchten Sie die Kontrolle übernehmen?',
            icon:    'pi pi-exclamation-triangle',
            accept:  async () => {
              try {
                resolve(await this.activateCheckout(token, true));
              } catch(forceError) {
                console.error('Failed to forcefully assign checkout register:', forceError);
                resolve(false);
              }
            },
            reject: () => {
              resolve(false);
            },
            acceptLabel: 'Ja, übernehmen',
            rejectLabel: 'Abbrechen',
          });
        });
      }

      return false;
    }
  }
}
